import { createContext, useContext, useState, useCallback } from "react";
import { rememberDeanConversation, useDeanSessionRevision } from "../lib/deanChatLifecycle";

interface DeanContextValue {
  openConversation: (conversationId: number | null) => void;
  requestedConversationId: number | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  clearRequestedConversation: () => void;
  newChatRevision: number;
}

const DeanContext = createContext<DeanContextValue | null>(null);

export function DeanProvider({ children }: { children: React.ReactNode }) {
  const revision = useDeanSessionRevision();
  return <ScopedDeanProvider key={revision}>{children}</ScopedDeanProvider>;
}

function ScopedDeanProvider({ children }: { children: React.ReactNode }) {
  const [requestedConversationId, setRequestedConversationId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [newChatRevision, setNewChatRevision] = useState(0);

  const openConversation = useCallback((conversationId: number | null) => {
    if (conversationId === null) {
      rememberDeanConversation(null);
      setNewChatRevision((revision) => revision + 1);
    }
    setRequestedConversationId(conversationId);
    setIsOpen(true);
  }, []);

  const clearRequestedConversation = useCallback(() => {
    setRequestedConversationId(null);
  }, []);

  return (
    <DeanContext.Provider value={{ openConversation, requestedConversationId, isOpen, setIsOpen, clearRequestedConversation, newChatRevision }}>
      {children}
    </DeanContext.Provider>
  );
}

export function useDean() {
  const ctx = useContext(DeanContext);
  if (!ctx) throw new Error("useDean must be used within DeanProvider");
  return ctx;
}
